import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
  Star, TrendingUp, TrendingDown, Plus, X, Search, RefreshCw,
  BarChart2, Globe, Coins, LineChart, Building2, Fuel,
  ArrowUp, ArrowDown, Info, DollarSign, Activity, Calendar, Target
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart as RechartsLineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { WATCHLIST_SLIDES } from '../config/tutorialSlides';
import { getSymbolName } from '../utils/symbolNames';
import HowItWorks from '../components/HowItWorks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Symbol logos mapping - crypto logos from CoinGecko/public CDNs
const SYMBOL_LOGOS = {
  // Cryptos
  'BTC': 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
  'ETH': 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
  'SOL': 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
  'XRP': 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png',
  'BNB': 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
  'ADA': 'https://assets.coingecko.com/coins/images/975/small/cardano.png',
  'DOGE': 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
  'AVAX': 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
  'LINK': 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',
  'DOT': 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png',
  'MATIC': 'https://assets.coingecko.com/coins/images/4713/small/polygon.png',
  'SHIB': 'https://assets.coingecko.com/coins/images/11939/small/shiba.png',
  'LTC': 'https://assets.coingecko.com/coins/images/2/small/litecoin.png',
  'ATOM': 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png',
  'UNI': 'https://assets.coingecko.com/coins/images/12504/small/uniswap-logo.png',
  'AAVE': 'https://assets.coingecko.com/coins/images/12645/small/AAVE.png',
  'FTM': 'https://assets.coingecko.com/coins/images/4001/small/Fantom_round.png',
  'NEAR': 'https://assets.coingecko.com/coins/images/10365/small/near.jpg',
  'APT': 'https://assets.coingecko.com/coins/images/26455/small/aptos_round.png',
  'ARB': 'https://assets.coingecko.com/coins/images/16547/small/photo_2023-03-29_21.47.00.jpeg',
  'OP': 'https://assets.coingecko.com/coins/images/25244/small/Optimism.png',
  'HYPE': 'https://assets.coingecko.com/coins/images/40006/small/hype_logo.png',
  'TAO': 'https://assets.coingecko.com/coins/images/28452/small/ARUsPeNQ_400x400.jpeg',
  'VET': 'https://assets.coingecko.com/coins/images/1167/small/VET_Token_Icon.png',
  'CHZ': 'https://assets.coingecko.com/coins/images/8834/small/CHZ_Token_updated.png',
  'PEPE': 'https://assets.coingecko.com/coins/images/29850/small/pepe-token.jpeg',
  'WIF': 'https://assets.coingecko.com/coins/images/33566/small/dogwifhat.jpg',
  'BONK': 'https://assets.coingecko.com/coins/images/28600/small/bonk.jpg',
  'SUI': 'https://assets.coingecko.com/coins/images/26375/small/sui_asset.jpeg',
  'INJ': 'https://assets.coingecko.com/coins/images/12882/small/Secondary_Symbol.png',
  'TIA': 'https://assets.coingecko.com/coins/images/31967/small/tia.jpg',
  'SEI': 'https://assets.coingecko.com/coins/images/28205/small/Sei_Logo_-_Transparent.png',
  'JUP': 'https://assets.coingecko.com/coins/images/34188/small/jup.png',
  'RENDER': 'https://assets.coingecko.com/coins/images/11636/small/rndr.png',
  'FET': 'https://assets.coingecko.com/coins/images/5681/small/Fetch.jpg',
  'ENS': 'https://assets.coingecko.com/coins/images/19785/small/acatxTm8_400x400.jpg',
  'MKR': 'https://assets.coingecko.com/coins/images/1364/small/Mark_Maker.png',
  'CRV': 'https://assets.coingecko.com/coins/images/12124/small/Curve.png',
  // Stocks
  'AAPL': 'https://logo.clearbit.com/apple.com',
  'TSLA': 'https://logo.clearbit.com/tesla.com',
  'NVDA': 'https://logo.clearbit.com/nvidia.com',
  'MSFT': 'https://logo.clearbit.com/microsoft.com',
  'GOOGL': 'https://logo.clearbit.com/google.com',
  'AMZN': 'https://logo.clearbit.com/amazon.com',
  'META': 'https://logo.clearbit.com/meta.com',
  'MC': 'https://logo.clearbit.com/lvmh.com',
  'NFLX': 'https://logo.clearbit.com/netflix.com',
  'AMD': 'https://logo.clearbit.com/amd.com',
  'INTC': 'https://logo.clearbit.com/intel.com',
  'CRM': 'https://logo.clearbit.com/salesforce.com',
  'ORCL': 'https://logo.clearbit.com/oracle.com',
  // Forex (flag emojis as fallback)
  'EUR': '🇪🇺',
  'USD': '🇺🇸',
  'GBP': '🇬🇧',
  'JPY': '🇯🇵',
  'AUD': '🇦🇺',
  'CHF': '🇨🇭',
  'CAD': '🇨🇦',
  'NZD': '🇳🇿',
  'CNY': '🇨🇳',
  // Commodities
  'GOLD': '🥇',
  'SILVER': '🥈',
  'OIL': '🛢️',
  'GAS': '⛽',
};

// Get logo for a symbol
const getSymbolLogo = (symbol, category) => {
  // For crypto, always use the category icon (Coins) - too many cryptos without logos
  if (category === 'crypto') {
    return null;
  }
  
  // Extract base symbol from formats like "BINANCE:BTCUSDT", "NASDAQ:AAPL", "FX:EURUSD"
  const parts = symbol.split(':');
  const ticker = parts[1] || parts[0];
  
  // For forex, get first currency
  let baseSymbol = ticker;
  if (category === 'forex') {
    baseSymbol = ticker.substring(0, 3);
  }
  
  return SYMBOL_LOGOS[baseSymbol.toUpperCase()];
};

// Generate realistic sparkline data based on price change percentage
const generateSparklineData = (currentPrice, changePercent, points = 20) => {
  if (!currentPrice) return [];
  
  const data = [];
  const change = changePercent || 0;
  
  // Amplify small changes for visual effect (minimum 3% visual swing)
  const visualSwing = Math.max(Math.abs(change), 3);
  const direction = change >= 0 ? 1 : -1;
  
  // Start price based on amplified change
  const startPrice = currentPrice * (1 - (direction * visualSwing / 100));
  
  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    
    // Base trend from start to end
    const trend = startPrice + (currentPrice - startPrice) * progress;
    
    // Add wave pattern for realism
    const waveStrength = Math.sin(progress * Math.PI) * 0.5; // Peak in middle
    const wave = Math.sin(i * 0.9) * (currentPrice * 0.008) * waveStrength;
    const wave2 = Math.cos(i * 1.3) * (currentPrice * 0.005) * waveStrength;
    
    data.push({ value: trend + wave + wave2 });
  }
  
  // Exact start/end
  data[0].value = startPrice;
  data[points - 1].value = currentPrice;
  
  return data;
};

// Mini Sparkline component  
const Sparkline = ({ data, isPositive }) => {
  if (!data || data.length < 2) return null;
  
  // Get min/max for domain
  const values = data.map(d => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  
  return (
    <div className="w-20 h-10">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data}>
          <YAxis domain={[minVal, maxVal]} hide />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={isPositive ? '#10b981' : '#ef4444'} 
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Symbol Logo component with fallback
const SymbolLogo = ({ symbol, name, category }) => {
  const [imgError, setImgError] = useState(false);
  const logo = getSymbolLogo(symbol, category);
  
  // Get category icon as fallback
  const getCategoryIcon = () => {
    const config = MARKET_CATEGORIES.find(c => c.id === category) || MARKET_CATEGORIES[0];
    const Icon = config.icon;
    return <Icon className={`w-5 h-5 ${config.color}`} />;
  };
  
  // If logo is an emoji (flag), display it directly
  if (logo && logo.length <= 4 && /\p{Emoji}/u.test(logo)) {
    return (
      <div className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center text-lg">
        {logo}
      </div>
    );
  }
  
  // If logo URL exists and no error
  if (logo && !imgError) {
    return (
      <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center overflow-hidden">
        <img 
          src={logo} 
          alt={name}
          className="w-6 h-6 object-contain"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  
  // Fallback to category icon
  return (
    <div className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center">
      {getCategoryIcon()}
    </div>
  );
};

// Market categories for suggestions
const MARKET_CATEGORIES = [
  {
    id: 'crypto',
    label: { fr: 'Crypto', en: 'Crypto' },
    icon: Coins,
    color: 'text-orange-400',
    examples: ['BINANCE:BTCUSDT', 'BINANCE:ETHUSDT', 'BINANCE:SOLUSDT', 'BYBIT:XRPUSDT']
  },
  {
    id: 'etf',
    label: { fr: 'ETF', en: 'ETF' },
    icon: Building2,
    color: 'text-cyan-400',
    examples: ['EURONEXT:CW8', 'AMEX:SPY', 'NASDAQ:QQQ', 'LSE:VWRL']
  },
  {
    id: 'forex',
    label: { fr: 'Forex', en: 'Forex' },
    icon: Globe,
    color: 'text-blue-400',
    examples: ['FX:EURUSD', 'FX:GBPUSD', 'FX:USDJPY', 'FX:AUDUSD']
  },
  {
    id: 'stocks',
    label: { fr: 'Actions', en: 'Stocks' },
    icon: LineChart,
    color: 'text-green-400',
    examples: ['NASDAQ:AAPL', 'NASDAQ:TSLA', 'NYSE:NVDA', 'EURONEXT:MC']
  },
  {
    id: 'indices',
    label: { fr: 'Indices', en: 'Indices' },
    icon: BarChart2,
    color: 'text-purple-400',
    examples: ['SP:SPX', 'TVC:DXY', 'CAPITALCOM:DE40', 'TVC:CAC40']
  },
  {
    id: 'commodities',
    label: { fr: 'Matières Premières', en: 'Commodities' },
    icon: Fuel,
    color: 'text-yellow-400',
    examples: ['TVC:GOLD', 'TVC:SILVER', 'NYMEX:CL1!', 'CBOT:ZC1!']
  }
];

// Check if input looks like an ISIN code
const isISIN = (input) => {
  // ISIN format: 2 letters + 9 alphanumeric + 1 check digit
  return /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(input.toUpperCase());
};

// Get currency symbol based on exchange or symbol
const getCurrencySymbol = (symbol, category) => {
  if (!symbol) return '$';
  
  const symbolUpper = symbol.toUpperCase();
  
  // European exchanges use EUR
  if (symbolUpper.startsWith('EURONEXT:') || 
      symbolUpper.startsWith('FP:') || 
      symbolUpper.startsWith('EP:') ||
      symbolUpper.startsWith('XETR:') ||
      symbolUpper.startsWith('MIL:') ||
      symbolUpper.startsWith('BME:')) {
    return '€';
  }
  
  // UK exchanges use GBP
  if (symbolUpper.startsWith('LSE:') || symbolUpper.startsWith('L:')) {
    return '£';
  }
  
  // Swiss exchanges use CHF
  if (symbolUpper.startsWith('SIX:') || symbolUpper.startsWith('SW:')) {
    return 'CHF ';
  }
  
  // Japanese exchanges use JPY
  if (symbolUpper.startsWith('TSE:') || symbolUpper.startsWith('TYO:')) {
    return '¥';
  }
  
  // ETFs - check if European based on ISIN pattern or category
  if (category === 'etf') {
    // If it's a European ETF (typically Euronext)
    if (symbolUpper.includes('EURONEXT') || symbolUpper.includes(':CW8') || 
        symbolUpper.includes(':CACC') || symbolUpper.includes(':EWLD') ||
        symbolUpper.includes(':MEUD') || symbolUpper.includes(':DFNS')) {
      return '€';
    }
  }
  
  // Default to USD for crypto, US stocks, etc.
  return '$';
};

// Search ETF by ISIN via backend API (OpenFIGI)
const searchETFByISIN = async (isin) => {
  try {
    const response = await axios.get(`${API}/etf/lookup-isin?isin=${isin.toUpperCase()}`);
    if (response.data.found && response.data.results.length > 0) {
      return response.data.results;
    }
    return null;
  } catch (error) {
    console.error('Error looking up ISIN:', error);
    return null;
  }
};

const Watchlist = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [symbolInput, setSymbolInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'change'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [autoscanAssets, setAutoscanAssets] = useState([]); // Assets selected for auto-scan
  const [selectedAsset, setSelectedAsset] = useState(null); // For detail modal
  const [assetDetails, setAssetDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Navigate to Analysis IA page with pre-selected symbol
  const handleAnalyzeAsset = (symbol, e) => {
    e.stopPropagation();
    // Navigate to analysis page with symbol as query parameter
    navigate(`/analysis?symbol=${encodeURIComponent(symbol)}`);
    toast.success(language === 'fr' ? `Analyse de ${symbol} lancée` : `Analyzing ${symbol}`);
  };

  // Fetch detailed info for an asset
  const fetchAssetDetails = async (symbol, category) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/market/asset-details?symbol=${encodeURIComponent(symbol)}`);
      setAssetDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch asset details:', error);
      // Use price data as fallback
      const priceData = prices[symbol];
      if (priceData) {
        setAssetDetails({
          symbol,
          current_price: priceData.current_price,
          change_percent: priceData.change_percent,
          high_24h: priceData.high_24h,
          low_24h: priceData.low_24h,
          high_52w: priceData.high_52w,
          low_52w: priceData.low_52w,
          market_cap: priceData.market_cap,
          volume_24h: priceData.volume_24h
        });
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open asset detail modal
  const openAssetDetail = (item) => {
    setSelectedAsset(item);
    setAssetDetails(null);
    fetchAssetDetails(item.symbol, item.category);
  };

  // Format large numbers
  const formatNumber = (num) => {
    if (!num) return '-';
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toLocaleString();
  };

  // Sort function for watchlist items
  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      const priceA = prices[a.symbol];
      const priceB = prices[b.symbol];
      
      let comparison = 0;
      switch (sortBy) {
        case 'price':
          comparison = (priceA?.current_price || 0) - (priceB?.current_price || 0);
          break;
        case 'change':
          comparison = (priceA?.change_percent || 0) - (priceB?.change_percent || 0);
          break;
        case 'name':
        default:
          comparison = a.name.localeCompare(b.name);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  // Toggle sort
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'change' ? 'desc' : 'asc'); // Default desc for change
    }
  };

  // LocalStorage key for guest watchlist
  const LOCAL_WATCHLIST_KEY = 'tradex_guest_watchlist';

  // Get watchlist from localStorage for guests
  const getLocalWatchlist = () => {
    try {
      const saved = localStorage.getItem(LOCAL_WATCHLIST_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse local watchlist:', e);
    }
    // Default watchlist
    return [
      { symbol: 'BINANCE:BTCUSDT', name: 'Bitcoin', category: 'crypto' },
      { symbol: 'FX:EURUSD', name: 'EUR/USD', category: 'forex' },
      { symbol: 'NASDAQ:AAPL', name: 'Apple Inc.', category: 'stocks' }
    ];
  };

  // Save watchlist to localStorage for guests
  const saveLocalWatchlist = (items) => {
    try {
      localStorage.setItem(LOCAL_WATCHLIST_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save local watchlist:', e);
    }
  };

  // Fetch user's watchlist from backend (authenticated) or localStorage (guest)
  const fetchWatchlist = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch watchlist
        const response = await axios.get(`${API}/watchlist/symbols`, { headers });
        setWatchlist(response.data.symbols || []);
        
        // Fetch auto-scan settings to know which assets have the star
        try {
          const autoscanRes = await axios.get(`${API}/autoscan/settings`, { headers });
          setAutoscanAssets(autoscanRes.data.selected_assets || []);
        } catch (e) {
          // Autoscan endpoint not available for this user tier
          console.debug('Autoscan settings not available:', e.message);
        }
      } catch (error) {
        console.error('Failed to fetch watchlist:', error);
        setWatchlist(getLocalWatchlist());
      }
    } else {
      // Guest: load from localStorage
      setWatchlist(getLocalWatchlist());
    }
    setLoading(false);
  }, [isAuthenticated]);

  // Fetch prices for all watchlist symbols
  const fetchPrices = useCallback(async () => {
    if (watchlist.length === 0) return;
    
    setLoadingPrices(true);
    try {
      const symbols = watchlist.map(item => item.symbol);
      const response = await axios.post(`${API}/market/prices`, symbols);
      setPrices(response.data.prices || {});
    } catch (error) {
      console.error('Failed to fetch prices:', error);
    } finally {
      setLoadingPrices(false);
    }
  }, [watchlist]);

  // Search for symbols
  const searchSymbols = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    
    try {
      // Check if it's an ISIN code first
      if (isISIN(query)) {
        const isinResults = await searchETFByISIN(query);
        if (isinResults && isinResults.length > 0) {
          setSearchResults(isinResults);
          setSearchLoading(false);
          return;
        }
      }
      
      // Use global Yahoo Finance search for comprehensive results
      // This covers ALL stocks, ETFs, indices worldwide (European, US, Asian, etc.)
      const response = await axios.get(`${API}/market/search-global?q=${encodeURIComponent(query)}&max_results=15`);
      
      if (response.data.results && response.data.results.length > 0) {
        setSearchResults(response.data.results);
      } else {
        // Fallback to local search if Yahoo returns nothing
        const fallbackResponse = await axios.get(`${API}/market/search?q=${encodeURIComponent(query)}`);
        setSearchResults(fallbackResponse.data.results || []);
      }
    } catch (error) {
      console.error('Failed to search symbols:', error);
      // Try fallback on error
      try {
        const fallbackResponse = await axios.get(`${API}/market/search?q=${encodeURIComponent(query)}`);
        setSearchResults(fallbackResponse.data.results || []);
      } catch {
        setSearchResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchSymbols(symbolInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [symbolInput, searchSymbols]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  // Fetch prices when watchlist changes
  useEffect(() => {
    if (watchlist.length > 0) {
      fetchPrices();
      // Refresh prices every 60 seconds
      const interval = setInterval(fetchPrices, 60000);
      return () => clearInterval(interval);
    }
  }, [watchlist, fetchPrices]);

  // Add symbol to watchlist
  const addToWatchlist = async (symbol, name = '', category = 'custom') => {
    const normalizedSymbol = symbol.toUpperCase().trim();
    
    if (watchlist.some(item => item.symbol === normalizedSymbol)) {
      toast.error(language === 'fr' ? 'Déjà dans vos favoris' : 'Already in watchlist');
      return;
    }

    const newItem = { 
      symbol: normalizedSymbol, 
      name: name || normalizedSymbol.split(':')[1] || normalizedSymbol,
      category 
    };

    // Update UI
    const updatedWatchlist = [...watchlist, newItem];
    setWatchlist(updatedWatchlist);
    setShowAddPanel(false);
    setSymbolInput('');
    setSearchResults([]);
    toast.success(language === 'fr' ? 'Ajouté aux favoris' : 'Added to watchlist');

    if (isAuthenticated) {
      // Save to backend for logged-in users
      try {
        await axios.post(`${API}/watchlist/symbols`, { symbol_data: newItem });
      } catch (error) {
        console.error('Failed to save to backend:', error);
      }
    } else {
      // Save to localStorage for guests
      saveLocalWatchlist(updatedWatchlist);
    }
  };

  // Remove symbol from watchlist
  const removeFromWatchlist = async (symbol, e) => {
    e.stopPropagation();
    
    const updatedWatchlist = watchlist.filter(item => item.symbol !== symbol);
    setWatchlist(updatedWatchlist);
    toast.success(language === 'fr' ? 'Retiré des favoris' : 'Removed from watchlist');
    
    if (isAuthenticated) {
      // Remove from backend for logged-in users
      try {
        await axios.delete(`${API}/watchlist/symbols/${encodeURIComponent(symbol)}`);
      } catch (error) {
        console.error('Failed to remove from backend:', error);
      }
    } else {
      // Update localStorage for guests
      saveLocalWatchlist(updatedWatchlist);
    }
  };

  // Get category config
  const getCategoryConfig = (categoryId) => {
    return MARKET_CATEGORIES.find(c => c.id === categoryId) || MARKET_CATEGORIES[0];
  };

  // Generate TradingView URL for a symbol
  const getTradingViewUrl = (symbol) => {
    // Clean the symbol for TradingView URL format
    // e.g., "BINANCE:BTCUSDT" -> "BINANCE:BTCUSDT"
    // e.g., "FX:EURUSD" -> "FX:EURUSD"
    // e.g., "BTC/USDT" -> "BINANCE:BTCUSDT"
    let tvSymbol = symbol;
    
    // If symbol doesn't have exchange prefix, add one based on content
    if (!symbol.includes(':')) {
      if (symbol.includes('/')) {
        // Crypto pair like BTC/USDT
        tvSymbol = `BINANCE:${symbol.replace('/', '')}`;
      } else if (symbol.includes('USD') && symbol.length <= 6) {
        // Forex pair like EURUSD
        tvSymbol = `FX:${symbol}`;
      } else {
        // Stock
        tvSymbol = `NASDAQ:${symbol}`;
      }
    }
    
    return `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tvSymbol)}`;
  };

  // Open TradingView for a symbol
  const openTradingView = (symbol, e) => {
    e.stopPropagation();
    window.open(getTradingViewUrl(symbol), '_blank');
  };

  // Main Watchlist View
  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8">
      {/* Onboarding Tutorial for new users */}
      <OnboardingTutorial
        pageId="watchlist"
        slides={WATCHLIST_SLIDES}
        storageKey="tutorial_watchlist_seen"
      />
      
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Star className="w-8 h-8 text-yellow-400" />
                {language === 'fr' ? 'Mes Marchés' : 'My Markets'}
              </h1>
              <HowItWorks pageKey="watchlist" />
            </div>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Crypto, Forex, Actions, Indices, Matières Premières' : 'Crypto, Forex, Stocks, Indices, Commodities'}
            </p>
          </div>
          <Button 
            onClick={() => setShowAddPanel(!showAddPanel)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {language === 'fr' ? 'Ajouter un symbole' : 'Add Symbol'}
          </Button>
        </div>

        {/* Add Symbol Panel */}
        {showAddPanel && (
          <Card className="bg-card border-white/10 animate-fade-in">
            <CardContent className="p-4 space-y-4">
              {/* Search Input */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Search className="w-4 h-4 text-primary" />
                  {language === 'fr' ? 'Rechercher un symbole' : 'Search for a symbol'}
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      placeholder={language === 'fr' ? "Tapez: BTC, AAPL, EURUSD, CW8, ou un code ISIN..." : "Type: BTC, AAPL, EURUSD, CW8, or an ISIN code..."}
                      value={symbolInput}
                      onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
                      className="bg-secondary/50 border-white/10 font-mono"
                    />
                    {searchLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { setShowAddPanel(false); setSymbolInput(''); setSearchResults([]); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'fr' 
                    ? '💡 Pour les ETF, vous pouvez taper le code ISIN (ex: FR0013412020, LU1681043599)' 
                    : '💡 For ETFs, you can type the ISIN code (e.g., FR0013412020, LU1681043599)'}
                </p>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-primary">
                    {language === 'fr' ? `${searchResults.length} résultat(s) trouvé(s)` : `${searchResults.length} result(s) found`}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {searchResults.map((result, index) => {
                      const isAlreadyAdded = watchlist.some(item => item.symbol === result.symbol);
                      
                      const handleClick = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isAlreadyAdded) {
                          addToWatchlist(result.symbol, result.name, result.category);
                        }
                      };
                      
                      return (
                        <button
                          key={`${result.symbol}-${index}`}
                          type="button"
                          onClick={handleClick}
                          disabled={isAlreadyAdded}
                          data-testid={`search-result-${result.symbol.replace(':', '-')}`}
                          className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                            isAlreadyAdded 
                              ? 'bg-secondary/20 border-white/5 opacity-50 cursor-not-allowed'
                              : 'bg-secondary/30 border-white/10 hover:border-primary/50 hover:bg-secondary/50 cursor-pointer'
                          }`}
                        >
                          <SymbolLogo symbol={result.symbol} name={result.name} category={result.category} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{result.name}</div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-muted-foreground font-mono">{result.symbol}</span>
                              {result.exchange && (
                                <span className="text-xs text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded">
                                  {result.exchange}
                                </span>
                              )}
                              {result.isin && (
                                <span className="text-xs text-cyan-400 font-mono bg-cyan-500/10 px-1.5 py-0.5 rounded">
                                  {result.isin}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Category badges */}
                          {result.category === 'etf' && (
                            <span className="text-xs text-cyan-400 px-2 py-0.5 rounded bg-cyan-500/20">
                              ETF
                            </span>
                          )}
                          {result.category === 'indices' && (
                            <span className="text-xs text-yellow-400 px-2 py-0.5 rounded bg-yellow-500/20">
                              Index
                            </span>
                          )}
                          {result.category === 'futures' && (
                            <span className="text-xs text-orange-400 px-2 py-0.5 rounded bg-orange-500/20">
                              Futures
                            </span>
                          )}
                          {result.category === 'stocks' && (
                            <span className="text-xs text-green-400 px-2 py-0.5 rounded bg-green-500/20">
                              {language === 'fr' ? 'Action' : 'Stock'}
                            </span>
                          )}
                          {isAlreadyAdded ? (
                            <span className="text-xs text-muted-foreground px-2 py-1 rounded bg-secondary/50">
                              {language === 'fr' ? 'Ajouté' : 'Added'}
                            </span>
                          ) : (
                            <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No results message */}
              {symbolInput.length >= 2 && !searchLoading && searchResults.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {language === 'fr' 
                      ? 'Aucun résultat. Essayez un autre terme ou entrez le symbole TradingView directement.' 
                      : 'No results. Try another term or enter the TradingView symbol directly.'}
                  </p>
                  {symbolInput.includes(':') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => addToWatchlist(symbolInput, symbolInput.split(':')[1] || symbolInput, 'custom')}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {language === 'fr' ? `Ajouter "${symbolInput}" quand même` : `Add "${symbolInput}" anyway`}
                    </Button>
                  )}
                </div>
              )}

              {/* Quick Add by Category (shown when no search) */}
              {symbolInput.length < 2 && (
                <div className="space-y-3 border-t border-white/10 pt-4">
                  <label className="text-sm font-medium">
                    {language === 'fr' ? 'Ou ajout rapide par catégorie' : 'Or quick add by category'}
                  </label>
                  {MARKET_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    return (
                      <div key={category.id} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${category.color}`} />
                          <span className="text-sm font-medium">{category.label[language === 'fr' ? 'fr' : 'en']}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {category.examples.map((symbol) => (
                            <button
                              key={symbol}
                              onClick={() => addToWatchlist(symbol, symbol.split(':')[1], category.id)}
                              disabled={watchlist.some(item => item.symbol === symbol)}
                              className={`px-3 py-1 rounded-lg text-xs font-mono transition-colors ${
                                watchlist.some(item => item.symbol === symbol)
                                  ? 'bg-secondary/30 text-muted-foreground cursor-not-allowed'
                                  : 'bg-secondary/50 hover:bg-secondary/70'
                              }`}
                            >
                              {symbol}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Watchlist by Category */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : watchlist.length > 0 ? (
          <>
            {/* Sort and Refresh controls */}
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              {/* Sort buttons */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground mr-1">
                  {language === 'fr' ? 'Trier par:' : 'Sort by:'}
                </span>
                <Button
                  variant={sortBy === 'name' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => toggleSort('name')}
                  className="h-7 text-xs"
                  data-testid="sort-by-name"
                >
                  {language === 'fr' ? 'Nom' : 'Name'}
                  {sortBy === 'name' && (
                    sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
                <Button
                  variant={sortBy === 'price' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => toggleSort('price')}
                  className="h-7 text-xs"
                  data-testid="sort-by-price"
                >
                  {language === 'fr' ? 'Prix' : 'Price'}
                  {sortBy === 'price' && (
                    sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
                <Button
                  variant={sortBy === 'change' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => toggleSort('change')}
                  className="h-7 text-xs"
                  data-testid="sort-by-change"
                >
                  {language === 'fr' ? 'Variation' : 'Change'}
                  {sortBy === 'change' && (
                    sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
              </div>
              
              {/* Refresh button */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchPrices}
                disabled={loadingPrices}
                className="text-muted-foreground h-7"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingPrices ? 'animate-spin' : ''}`} />
                {language === 'fr' ? 'Actualiser' : 'Refresh'}
              </Button>
            </div>
            
            {/* Group watchlist by category */}
            {(() => {
              // Group items by category
              const grouped = watchlist.reduce((acc, item) => {
                const cat = item.category || 'custom';
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
                return acc;
              }, {});
              
              // Define category order
              const categoryOrder = ['crypto', 'etf', 'forex', 'stocks', 'indices', 'commodities', 'custom'];
              
              return categoryOrder.map(categoryId => {
                const items = grouped[categoryId];
                if (!items || items.length === 0) return null;
                
                // Sort items within category
                const sortedItems = sortItems(items);
                
                const config = getCategoryConfig(categoryId);
                const CategoryIcon = config.icon;
                
                return (
                  <div key={categoryId} className="mb-6">
                    {/* Category Header */}
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                      <CategoryIcon className={`w-5 h-5 ${config.color}`} />
                      <h2 className={`text-lg font-bold ${config.color}`}>
                        {config.label[language === 'fr' ? 'fr' : 'en']}
                      </h2>
                      <span className="text-xs text-muted-foreground bg-secondary/50 px-2 py-0.5 rounded-full">
                        {sortedItems.length}
                      </span>
                    </div>
                    
                    {/* Items List */}
                    <div className="space-y-1">
                      {sortedItems.map((item) => {
                        const priceData = prices[item.symbol];
                        const hasPrice = priceData && priceData.current_price;
                        const isPositive = priceData?.change_percent >= 0;
                        
                        // Generate realistic sparkline data based on actual price change
                        const sparklineData = hasPrice ? 
                          generateSparklineData(priceData.current_price, priceData.change_percent || 0) : [];
                        
                        return (
                          <div
                            key={item.symbol}
                            data-testid={`watchlist-item-${item.symbol.replace(':', '-')}`}
                            onClick={() => openAssetDetail(item)}
                            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg bg-card/50 hover:bg-card border border-transparent hover:border-primary/30 transition-all group cursor-pointer"
                          >
                            {/* Mobile: Row 1 - Star, Logo, Name */}
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                              {/* Auto-scan Star indicator */}
                              {autoscanAssets.includes(item.symbol) && (
                                <div className="flex-shrink-0" title="Asset prioritaire pour scan auto">
                                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                                </div>
                              )}
                              
                              {/* Logo */}
                              <SymbolLogo symbol={item.symbol} name={item.name} category={item.category} />
                              
                              {/* Name & Symbol */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate text-sm sm:text-base">{getSymbolName(item.symbol) || item.name}</span>
                                </div>
                                <span className="text-xs text-muted-foreground font-mono truncate block max-w-[120px] sm:max-w-none">{item.symbol}</span>
                              </div>
                            </div>
                            
                            {/* Mobile: Row 2 - Price, Change, Buttons */}
                            <div className="flex items-center justify-end gap-2 sm:gap-3 pl-0 sm:pl-0">
                              {/* Sparkline - hidden on mobile */}
                              <div className="hidden md:block">
                                <Sparkline data={sparklineData} isPositive={isPositive} />
                              </div>
                            
                              {/* Price */}
                              <div className="text-right min-w-[70px] sm:min-w-[90px]">
                              {hasPrice ? (
                                <span className="font-mono font-bold text-sm sm:text-base">
                                  {getCurrencySymbol(item.symbol, item.category)}{priceData.current_price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </div>
                            
                            {/* Change % */}
                            <div className="min-w-[65px] sm:min-w-[75px] text-right">
                              {hasPrice ? (
                                <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-xs sm:text-sm font-mono font-bold ${
                                  isPositive ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'
                                }`}>
                                  {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                  {isPositive ? '+' : ''}{priceData.change_percent?.toFixed(2)}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </div>
                            
                            {/* 24h High/Low combined */}
                            <div className="hidden lg:flex flex-col min-w-[100px] text-right text-xs">
                              {hasPrice && priceData.high_24h ? (
                                <>
                                  <span><span className="text-primary/70">H:</span> <span className="font-mono">{getCurrencySymbol(item.symbol, item.category)}{priceData.high_24h?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></span>
                                  <span><span className="text-destructive/70">L:</span> <span className="font-mono">{getCurrencySymbol(item.symbol, item.category)}{priceData.low_24h?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></span>
                                </>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                            
                            {/* Info icon */}
                            <div className="p-1.5 sm:p-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg flex items-center gap-1.5 transition-colors">
                              <Info className="w-4 h-4 text-primary" />
                            </div>
                            
                            {/* Analyze button */}
                            <button
                              onClick={(e) => handleAnalyzeAsset(item.symbol, e)}
                              data-testid={`analyze-${item.symbol.replace(':', '-')}`}
                              className="p-1.5 sm:p-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg flex items-center gap-1.5 transition-colors"
                              title={language === 'fr' ? 'Analyser cet actif' : 'Analyze this asset'}
                            >
                              <Target className="w-4 h-4 text-primary" />
                            </button>
                            
                            {/* Delete button */}
                            <button
                              onClick={(e) => removeFromWatchlist(item.symbol, e)}
                              data-testid={`remove-${item.symbol.replace(':', '-')}`}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/20 rounded"
                            >
                              <X className="w-4 h-4 text-destructive" />
                            </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </>
        ) : (
          <Card className="bg-card border-white/10">
            <CardContent className="p-12 text-center">
              <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">
                {language === 'fr' ? 'Aucun symbole' : 'No symbols yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {language === 'fr' 
                  ? 'Ajoutez des symboles à votre liste de suivi' 
                  : 'Add symbols to your watchlist'}
              </p>
              <Button onClick={() => setShowAddPanel(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Ajouter un symbole' : 'Add a symbol'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Asset Detail Modal */}
      <Dialog open={!!selectedAsset} onOpenChange={() => setSelectedAsset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedAsset && (
                <>
                  <SymbolLogo symbol={selectedAsset.symbol} name={selectedAsset.name} category={selectedAsset.category} />
                  <div>
                    <div className="text-lg font-bold">{selectedAsset.name}</div>
                    <div className="text-sm text-muted-foreground font-mono">{selectedAsset.symbol}</div>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : assetDetails ? (
            <div className="space-y-4">
              {/* Current Price */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">{language === 'fr' ? 'Prix actuel' : 'Current Price'}</span>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold font-mono">
                    {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                    {assetDetails.current_price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}
                  </div>
                  {assetDetails.change_percent !== undefined && (
                    <div className={`text-sm font-mono ${assetDetails.change_percent >= 0 ? 'text-primary' : 'text-destructive'}`}>
                      {assetDetails.change_percent >= 0 ? '+' : ''}{assetDetails.change_percent?.toFixed(2)}%
                    </div>
                  )}
                </div>
              </div>

              {/* 24h Range */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown className="w-4 h-4 text-destructive" />
                    <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Plus bas 24h' : '24h Low'}</span>
                  </div>
                  <div className="font-mono font-bold">
                    {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                    {assetDetails.low_24h?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Plus haut 24h' : '24h High'}</span>
                  </div>
                  <div className="font-mono font-bold">
                    {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                    {assetDetails.high_24h?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                  </div>
                </div>
              </div>

              {/* 52 Week Range */}
              {(assetDetails.high_52w || assetDetails.low_52w) && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Plus bas 52 sem.' : '52w Low'}</span>
                    </div>
                    <div className="font-mono font-bold">
                      {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                      {assetDetails.low_52w?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Plus haut 52 sem.' : '52w High'}</span>
                    </div>
                    <div className="font-mono font-bold">
                      {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                      {assetDetails.high_52w?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                    </div>
                  </div>
                </div>
              )}

              {/* All-Time High/Low (ATH/ATL) */}
              {(assetDetails.all_time_high || assetDetails.all_time_low) && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-red-300">{language === 'fr' ? 'Plus bas historique' : 'All-Time Low'}</span>
                    </div>
                    <div className="font-mono font-bold text-red-400">
                      {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                      {assetDetails.all_time_low?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4}) || '-'}
                    </div>
                    {assetDetails.pct_from_atl && (
                      <div className="text-xs text-emerald-400 mt-1">
                        +{assetDetails.pct_from_atl?.toLocaleString()}% depuis ATL
                      </div>
                    )}
                  </div>
                  <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-300">{language === 'fr' ? 'Plus haut historique' : 'All-Time High'}</span>
                    </div>
                    <div className="font-mono font-bold text-emerald-400">
                      {selectedAsset && getCurrencySymbol(selectedAsset.symbol, selectedAsset.category)}
                      {assetDetails.all_time_high?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                    </div>
                    {assetDetails.pct_from_ath && (
                      <div className="text-xs text-red-400 mt-1">
                        {assetDetails.pct_from_ath?.toLocaleString()}% depuis ATH
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Market Cap & Volume */}
              <div className="grid grid-cols-2 gap-3">
                {assetDetails.market_cap && (
                  <div className="p-3 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <Activity className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-muted-foreground">Market Cap</span>
                    </div>
                    <div className="font-mono font-bold">
                      ${formatNumber(assetDetails.market_cap)}
                    </div>
                  </div>
                )}
                {assetDetails.volume_24h && (
                  <div className="p-3 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart2 className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Volume 24h' : '24h Volume'}</span>
                    </div>
                    <div className="font-mono font-bold">
                      ${formatNumber(assetDetails.volume_24h)}
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Info */}
              {assetDetails.description && (
                <div className="p-3 rounded-xl bg-white/5">
                  <div className="text-xs text-muted-foreground mb-2">{language === 'fr' ? 'Description' : 'Description'}</div>
                  <p className="text-sm text-gray-300 line-clamp-3">{assetDetails.description}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Watchlist;
